import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useApiCallService from '../../Common/Helpers/ApiCall';
import { useTranslation } from 'react-i18next';
import { CheckUserPermission } from '../../AppSystem/Helpers/CheckUserPermission';
import { Spinner } from 'reactstrap';
import { SlidePanel } from '../../Common/Components/Modal/SlidePanel';
import { forwardRef, useImperativeHandle } from 'react';

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image', 'video'],
        ['clean'],
    ],
};
const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'image', 'video'];

const isContentEmpty = (html) =>
    !html || html.trim() === '' || html === '<p><br></p>';

const normalizeOrders = (arr) =>
    arr.map((a, i) => ({ ...a, order: i + 1 }));

// ── ArticleForm ───────────────────────────────────────────────────────────────
export function ArticleForm({ initial = {}, totalArticles, onSave, onCancel, saving, onReview, onDelete = null, canDelete = false, isEdit = false }) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const [visible,  setVisible]  = useState(initial.isVisible ?? false);
    const [title,    setTitle]    = useState(initial.title ?? '');
    const [content,  setContent]  = useState(initial.content ?? '');
    const [reviewDate, setReviewDate] = useState(initial.reviewDate ?? null);
    const isNew = !initial.id;
    const isEditing = isEdit || !isNew;
    const [position, setPosition] = useState(initial.order ?? (totalArticles + 1));
    const optionCount = isNew ? totalArticles + 1 : totalArticles;
    const canSave = title.trim() !== '' && !isContentEmpty(content) && !saving;

    const handleReview = async () => {
        await ApiCall(
            { method: 'put', url: `/guide/ReviewArticle?articleId=${encodeURIComponent(initial.id)}` },
            () => setReviewDate(new Date().toISOString())
        );
    };

    return (
        <div className="hp-form-wrapper">
            {/* ── Topbar ── */}
            <div className="hp-form-topbar">
                <div className={`hp-form-topbar-actions${isEdit ? ' hp-form-topbar-actions--guide' : ''}`}>
                    <button
                        className="hp-btn-save-top"
                        onClick={() => onSave({ title, content, isVisible: visible, order: position })}
                        disabled={!canSave}
                    >
                        {saving && <Spinner size="sm" color="light" />}
                        {t('button.save') || 'Spara'}
                    </button>
                    <button className="hp-btn-back-top" onClick={onCancel} disabled={saving}>
                        {t('button.back') || 'Gå tillbaka'}
                    </button>
                    {isEditing && canDelete && typeof onDelete === 'function' && (
                        <button className="hp-btn-delete-top" onClick={onDelete} disabled={saving}>
                            {t('button.delete') || 'Delete'}
                        </button>
                    )}
                </div>
            </div>
            <div className="hp-form-topbar">
                <div className="hp-radio-group">
                    <label className={`hp-radio-label ${visible ? 'hp-radio-label--active' : ''}`}>
                        <input type="radio" name="visibility" checked={visible} onChange={() => setVisible(true)} />
                        <span className="hp-radio-dot" />
                        {t('guide.visible')}
                    </label>
                    <label className={`hp-radio-label ${!visible ? 'hp-radio-label--active' : ''}`}>
                        <input type="radio" name="visibility" checked={!visible} onChange={() => setVisible(false)} />
                        <span className="hp-radio-dot" />
                        {t('guide.hidden') || 'Dold'}
                    </label>
                </div>
            </div>

            {/* ── Review — only in edit mode ── */}
            {/*!isNew && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: '1px solid #f0ebe3', background: '#fafaf9' }}>
                    <button className="hp-btn-review-top" onClick={handleReview}>
                        <i className="bx bx-check-shield" style={{ marginRight: 4 }} />
                        {t('guide.markReviewed')}
                    </button>
                    <span style={{ fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="bx bx-time-five" />
                        {reviewDate
                            ? `${t('guide.lastReviewed')}: ${new Date(reviewDate).toLocaleDateString()}`
                            : t('guide.neverReviewed')}
                    </span>
                </div>
            )*/}

            {/* ── Titel + Position ── */}
            <div className="hp-form-row">
                <div className="hp-form-field hp-form-field--grow">
                    <label className="hp-form-label">{t('modelLabel.title') || 'Titel'}</label>
                    <input
                        className="hp-form-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('modelLabel.title') || 'Titel'}
                    />
                </div>
                <div className="hp-form-field hp-form-field--position">
                    <label className="hp-form-label">{t('modelLabel.position') || 'Position'}</label>
                    <select
                        className="hp-form-select"
                        value={position}
                        onChange={(e) => setPosition(Number(e.target.value))}
                    >
                        {Array.from({ length: optionCount }, (_, i) => i + 1).map(n => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* ── Innehåll ── */}
            <div className="hp-form-field hp-form-field--editor">
                <label className="hp-form-label">{t('modelLabel.content') || 'Innehåll'}</label>
                <div className="hp-editor-wrap">
                    <ReactQuill
                        theme="snow"
                        value={content}
                        onChange={setContent}
                        modules={modules}
                        formats={formats}
                        placeholder="..."
                    />
                </div>
            </div>
        </div>
    );
}

// ── ArticleCard ───────────────────────────────────────────────────────────────
function ArticleCard({ article, index, canEdit, showBadge, onEdit, onDelete, onDragStart, onDragOver, onDrop, isDraggingOver, isExpanded, onToggle }) {
    const { t } = useTranslation();

    return (
        <div
            className={`hp-card ${isDraggingOver ? 'hp-card--drag-over' : ''}`}
            draggable={canEdit}
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
            onDrop={() => onDrop(index)}
        >
            <button className="hp-card-header" onClick={() => onToggle(article.id)}>
                <div className="hp-card-left">
                    {canEdit && (
                        <span className="hp-drag-handle" title="Dra för att ändra ordning">
                            <i className="bx bx-grid-vertical" />
                        </span>
                    )}
                    {/*<span className="hp-position-badge">{article.order}</span>*/}
                    {showBadge && (
                        <span className={`hp-badge ${article.isVisible ? 'hp-badge--visible' : 'hp-badge--hidden'}`}>
                            {article.isVisible ? (t('guide.visible') || 'Synlig') : (t('guide.hidden') || 'Dold')}
                        </span>
                    )}
                    <span className="hp-card-title">{article.title || '(ingen titel)'}</span>
                </div>
                <div className="hp-card-right">
                    {canEdit && (
                        <>
                            <button
                                className="hp-icon-btn hp-icon-btn--edit"
                                onClick={(e) => { e.stopPropagation(); onEdit(article); }}
                                title={t('button.edit')}
                            >
                                <i className="bx bxs-pencil" />
                            </button>
                            <button
                                className="hp-icon-btn hp-icon-btn--delete"
                                onClick={(e) => { e.stopPropagation(); onDelete(article); }}
                                title={t('button.delete')}
                            >
                                <i className="bx bx-trash" />
                            </button>
                        </>
                    )}
                    <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'} hp-chevron`} />
                </div>
            </button>

            {isExpanded && (
                <div className="hp-card-body">
                    {!isContentEmpty(article.content) ? (
                        <div
                            className="hp-content"
                            dangerouslySetInnerHTML={{
                                __html: (article.content ?? '')
                                    .replace(/<img /g, '<img style="max-width:100%;height:auto;display:block;margin:0.5rem 0;" ')
                                    .replace(/<iframe /g, '<iframe style="max-width:100%;width:100%;aspect-ratio:16/9;height:auto;display:block;margin:0.5rem 0;" ')
                                    .replace(/<video /g, '<video style="max-width:100%;height:auto;display:block;margin:0.5rem 0;" controls ')
                            }}
                        />
                    ) : (
                        <p style={{ color: '#9ca3af', fontSize: 13, margin: 0, fontStyle: 'italic' }}>
                            {t('guide.noContent') || 'Inget innehåll ännu.'}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

// ── HelpPanel ─────────────────────────────────────────────────────────────────
export function HelpPanel({ routeId: routeIdProp, initialEditArticle = null, editModeActive: editModeActiveProp = false }) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const location = useLocation();

    const canEdit = (
        CheckUserPermission("system.setting.guide.update") ||
        CheckUserPermission("system.setting.guide") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system")
    );

    const routeId = location.pathname;

    const [guideDoc,          setGuideDoc]          = useState(null);
    const [loading,           setLoading]           = useState(true);
    const [saving,            setSaving]            = useState(false);
    const [reordering,        setReordering]        = useState(false);
    const [mode,              setMode]              = useState(initialEditArticle ? 'edit' : null);
    const [editingArticle,    setEditingArticle]    = useState(initialEditArticle ?? null);
    const [expandedArticleId, setExpandedArticleId] = useState(null);
    const [editModeActive,    setEditModeActive]    = useState(editModeActiveProp);
    const dragIndexRef = useRef(null);
    const [dragOver,    setDragOver]   = useState(null);
    const [localArticles, setLocalArticles] = useState([]);

    useEffect(() => {
        setEditModeActive(editModeActiveProp);
    }, [editModeActiveProp]);

    // If there's an initialEditArticle, automatically activate edit mode
    const initialEditArticleId = initialEditArticle?.id ?? null;
    useEffect(() => {
        if (initialEditArticleId) {
            setEditingArticle(initialEditArticle);
            setMode('edit');
            setEditModeActive(true);
        }
    }, [initialEditArticleId]);

    const handleToggleArticle = (articleId) => {
        setExpandedArticleId(prev => prev === articleId ? null : articleId);
    };

    const loadGuide = useCallback(() => {
        setLoading(true);
        ApiCall(
            { method: 'get', url: `/guide/by-route?routeId=${encodeURIComponent(routeId)}` },
            (res) => {
                const doc = Array.isArray(res) ? (res[0] ?? null) : (res ?? null);
                setGuideDoc(doc);
                const sorted = normalizeOrders(
                    [...(doc?.articles ?? [])]
                        .filter(a => canEdit || a.isVisible)
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                );
                setLocalArticles(sorted);
                setLoading(false);
            }
        ).catch(() => setLoading(false));
    }, [routeId]);

    useEffect(() => {
        loadGuide();
        // Only reset mode if there's no initialEditArticle
        if (!initialEditArticle) {
            setMode(null);
            setEditingArticle(null);
        }
    }, [routeId]);

    
    // Sincronizes localArticles with guideDoc when it changes (after saving)
    useEffect(() => {
        if (!guideDoc) return;
        const sorted = normalizeOrders(
            [...(guideDoc.articles ?? [])]
                .filter(a => canEdit || a.isVisible)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        setLocalArticles(sorted);
    }, [guideDoc]);

    // ── Reorder -> backend ────────────────────────────────────────────────────
    const saveReorder = useCallback(async (ordered) => {
        setReordering(true);
        try {
            await ApiCall(
                {
                    method: 'put',
                    url: `/guide/reorder?routeId=${encodeURIComponent(routeId)}`,
                    data: { items: ordered.map(a => ({ articleId: a.id, order: a.order })) },
                },
                (res) => setGuideDoc(res)
            );
        } finally { setReordering(false); }
    }, [routeId]);

    const handleDragStart = (index) => { dragIndexRef.current = index; };
    const handleDragOver  = (index) => { setDragOver(index); };

    const handleDrop = (dropIndex) => {
        const dragIndex = dragIndexRef.current;
        if (dragIndex === null || dragIndex === dropIndex) {
            dragIndexRef.current = null; setDragOver(null); return;
        }

        const reordered = [...localArticles];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        const normalized = normalizeOrders(reordered);

        setLocalArticles(normalized);
        dragIndexRef.current = null; setDragOver(null);
        saveReorder(normalized);
    };

    // ── Create ─────────────────────────────────────────────────────────────────
    const handleCreate = async ({ title, content, isVisible, order }) => {
        setSaving(true);
        try {
            const shifted = localArticles.map(a => ({
                ...a, order: a.order >= order ? a.order + 1 : a.order,
            }));
            await ApiCall(
                { method: 'post', url: `/guide/article?routeId=${encodeURIComponent(routeId)}`, data: { articleId: null, title, content, isVisible, order } },
                async (res) => {
                    if (shifted.some((a, i) => a.order !== localArticles[i]?.order)) {
                        const normalized = normalizeOrders([...(res.articles ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
                        await ApiCall(
                            { method: 'put', url: `/guide/reorder?routeId=${encodeURIComponent(routeId)}`, data: { items: normalized.map(a => ({ articleId: a.id, order: a.order })) } },
                            (res2) => { setGuideDoc(res2); setMode(null); }
                        );
                    } else { setGuideDoc(res); setMode(null); }
                }
            );
        } finally { setSaving(false); }
    };

    // ── Update ────────────────────────────────────────────────────────────
    const handleUpdate = async ({ title, content, isVisible, order }) => {
        setSaving(true);
        try {
            const oldOrder = editingArticle.order;
            const articleId = editingArticle.id;

            // First we update the content
            await ApiCall(
                { method: 'put', url: `/guide/article?routeId=${encodeURIComponent(routeId)}&articleId=${encodeURIComponent(articleId)}`, data: { articleId, title, content, isVisible, order } },
                async (res) => {
                    if (oldOrder !== order) {
                        const withNew = (res.articles ?? []).map(a => a.id === articleId ? { ...a, order } : a);
                        const others = withNew.filter(a => a.id !== articleId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                        others.splice(order - 1, 0, { ...editingArticle, order });
                        const normalized = normalizeOrders(others);

                        await ApiCall(
                            { method: 'put', url: `/guide/reorder?routeId=${encodeURIComponent(routeId)}`, data: { items: normalized.map(a => ({ articleId: a.id, order: a.order })) } },
                            (res2) => { setGuideDoc(res2); setMode(null); setEditingArticle(null); }
                        );
                    } else { setGuideDoc(res); setMode(null); setEditingArticle(null); }
                }
            );
        } finally { setSaving(false); }
    };

    // ── Delete ──────────────────────────────────────────────────────────────
    const handleDelete = async (article) => {
        if (!window.confirm(t('guide.confirmDelete') || 'Ta bort denna artikel?')) return;
        await ApiCall(
            { method: 'delete', url: `/guide/article?routeId=${encodeURIComponent(routeId)}&articleId=${encodeURIComponent(article.id)}` },
            async (res) => {
                const normalized = normalizeOrders([...(res.articles ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
                if (normalized.length > 0) {
                    await ApiCall(
                        { method: 'put', url: `/guide/reorder?routeId=${encodeURIComponent(routeId)}`, data: { items: normalized.map(a => ({ articleId: a.id, order: a.order })) } },
                        (res2) => setGuideDoc(res2)
                    );
                } else { setGuideDoc(res); }
            }
        );
    };
    
    // ── Edit ────────────────────────────────────────────────────────────────
    const handleEdit   = (article) => { setEditingArticle(article); setMode('edit'); };
    const handleCancel = () => { setMode(null); setEditingArticle(null); };

    // ── Render formulario ─────────────────────────────────────────────────────
    if (mode === 'new' || (mode === 'edit' && editingArticle)) {
        return (
            <>
                {/* The list still visible behind */}
                <div className="hp-wrapper">
                    <div className="hp-list-header">
                        <span className="hp-route-badge">{routeId}</span>
                    </div>
                </div>

                <SlidePanel
                    isOpen={true}
                    onClose={handleCancel}
                    title={mode === 'new' ? (t('guide.newGuide') || 'Ny artikel') : t('button.edit')}
                    variant="center"
                    zIndexOffset={20}
                >
                    <ArticleForm
                        initial={mode === 'edit' ? editingArticle : {}}
                        totalArticles={localArticles.length}
                        onSave={mode === 'new' ? handleCreate : handleUpdate}
                        onCancel={handleCancel}
                        saving={saving}
                    />
                </SlidePanel>
            </>
        );
    }

    // ── Render lista ──────────────────────────────────────────────────────────
    // isEditing = edit mode activo Y tiene permisos
    const isEditing = editModeActive && canEdit;

    return (
        <div className="hp-wrapper">
            <div className="hp-list-header">
                {isEditing && (
                    <span className="hp-route-badge">{t(guideDoc.content) ?? routeId}</span>
                )}
                <div className="hp-list-header-right">
                    {reordering && <Spinner size="sm" color="success" />}

                    {/* Botón de nuevo artículo — solo en modo edición */}
                    {isEditing && (
                        <button className="hp-btn-new" onClick={() => setMode('new')}>
                            <i className="bx bx-plus" />
                            {t('guide.newGuide') || 'Ny artikel'}
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="hp-loading"><Spinner color="success" /></div>

            ) : localArticles.length === 0 ? (
                <div className="hp-empty">
                    <i className="bx bx-book-open hp-empty-icon" />
                    <p className="hp-empty-text">{t('guide.noGuide') || 'Inga artiklar ännu för denna vy.'}</p>
                    {isEditing && (
                        <button className="hp-btn-new" onClick={() => setMode('new')}>
                            <i className="bx bx-plus" />
                            {t('guide.createGuide') || 'Skapa artikel'}
                        </button>
                    )}
                </div>

            ) : (
                <div className="hp-list">
                    {localArticles
                        .filter(a => isEditing || a.isVisible)
                        .map((article, index) => (
                            <ArticleCard
                                key={article.id}
                                article={article}
                                index={index}
                                canEdit={isEditing}
                                showBadge={isEditing}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                isDraggingOver={dragOver === index}
                                isExpanded={expandedArticleId === article.id}
                                onToggle={handleToggleArticle}
                            />
                        ))}
                </div>
            )}
        </div>
    );
}
